import React, { useEffect, useState } from "react";
import { apiAppFetch } from '../utils/apiFetch';
import './Ledger.css';

// Convert PHP getStartDateOfWeek function - Week starts from Sunday
const getStartDateOfWeek = (weekNumber, year) => {
    // PHP: $date->setISODate($year, $weekNumber, 1) - Set to Monday of ISO week
    // Then: $date->modify('last sunday') - Get the Sunday before that Monday
    
    // Calculate ISO week Monday first
    // January 4 is always in ISO week 1, find the Monday of that week
    const jan4 = new Date(year, 0, 4);
    const jan4Day = jan4.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
    
    // Find Monday of ISO week 1
    let firstMonday;
    if (jan4Day === 0) {
        // Jan 4 is Sunday, go back 6 days to get Monday (Dec 29)
        firstMonday = new Date(year, 0, 4 - 6);
    } else if (jan4Day === 1) {
        // Jan 4 is Monday, it's already the start
        firstMonday = new Date(year, 0, 4);
    } else {
        // Jan 4 is Tuesday-Saturday, go back (jan4Day - 1) days
        firstMonday = new Date(year, 0, 4 - (jan4Day - 1));
    }
    
    // Calculate the Monday of the requested ISO week
    const weekMonday = new Date(firstMonday);
    weekMonday.setDate(firstMonday.getDate() + (weekNumber - 1) * 7);
    
    // PHP: $date->modify('last sunday') - From Monday, go back to previous Sunday
    const weekStart = new Date(weekMonday);
    weekStart.setDate(weekMonday.getDate() - 1); // Monday - 1 = Sunday
    
    // Format as YYYY-MM-DD without timezone conversion
    const startYear = weekStart.getFullYear();
    const month = String(weekStart.getMonth() + 1).padStart(2, '0');
    const day = String(weekStart.getDate()).padStart(2, '0');
    return `${startYear}-${month}-${day}`;
};

// Convert PHP getEndDateOfWeek function - Updated for Sunday-Saturday week
const getEndDateOfWeek = (weekNumber, year) => {
    // PHP: $date->setISODate($year, $weekNumber, 1) - Set to Monday of ISO week
    // Then: $date->modify('saturday this week') - Get Saturday of that week
    
    // Calculate ISO week Monday first - same logic as getStartDateOfWeek
    const jan4 = new Date(year, 0, 4);
    const jan4Day = jan4.getDay();
    
    let firstMonday;
    if (jan4Day === 0) {
        firstMonday = new Date(year, 0, 4 - 6);
    } else if (jan4Day === 1) {
        firstMonday = new Date(year, 0, 4);
    } else {
        firstMonday = new Date(year, 0, 4 - (jan4Day - 1));
    }
    
    // Calculate the Monday of the requested ISO week
    const weekMonday = new Date(firstMonday);
    weekMonday.setDate(firstMonday.getDate() + (weekNumber - 1) * 7);
    
    // PHP: $date->modify('saturday this week') - Get Saturday of that week
    // From Monday, add 5 days to get Saturday (Monday + 5 = Saturday)
    const weekEnd = new Date(weekMonday);
    weekEnd.setDate(weekMonday.getDate() + 5);
    
    // Format as YYYY-MM-DD without timezone conversion
    const endYear = weekEnd.getFullYear();
    const endMonth = String(weekEnd.getMonth() + 1).padStart(2, '0');
    const endDay = String(weekEnd.getDate()).padStart(2, '0');
    return `${endYear}-${endMonth}-${endDay}`;
};

// Format date as d-M-Y (e.g., 01-Jan-2024)
const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

// Round function similar to PHP round
const round = (number, decimalPlaces = 2) => {
    return Number(Number(number).toFixed(decimalPlaces));
};

// Get ISO week number (PHP date('W'))
const getWeekNumber = (dateString) => {
    const date = new Date(dateString);
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

// Get ISO year (PHP date('o'))
const getISOYear = (dateString) => {
    const date = new Date(dateString);
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    return d.getUTCFullYear();
};

// Get day number of week (1=Monday, 7=Sunday) - PHP date('N')
const getDayNumberOfWeek = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDay();
    return day === 0 ? 7 : day;
};

const Ledger = () => {
    const [data, setData] = useState([]);
    const [openingBalance, setOpeningBalance] = useState(0);
    const [groupedTradeOrders, setGroupedTradeOrders] = useState({});
    const [userRole, setUserRole] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    useEffect(() => {
        const fetchData = async () => {
            const role = localStorage.getItem('userRole') || 'E';
            setUserRole(role);
            
            const response = await apiAppFetch('ledger', {
                method: 'POST',
                body: JSON.stringify({}),
            });
            const dataa = await response.json();
            processData(dataa);
        };

        fetchData();
    }, []);

    // Convert PHP opening balance calculation
    const calculateOpeningBalance = (dataa) => {
        // Handle both array and object responses
        let dataArray = Array.isArray(dataa) ? dataa : (dataa && typeof dataa === 'object' ? Object.values(dataa) : []);
        
        if (dataArray && dataArray.length > 0) {
            const firstData = dataArray[0];
            const first_mainAmount = firstData.mainAmount;
            const first_transactionAmount = firstData.transactionAmount;
            let first_transactionType = firstData.transactionType;

            let openingBalance = 0;

            if (first_transactionType === 1) {
                openingBalance = first_mainAmount - first_transactionAmount;
            } else if (first_transactionType === 2) {
                openingBalance = first_mainAmount + first_transactionAmount;
            } else if (first_transactionType === 3) {
                first_transactionType = firstData.jvTransactionType;
                if (first_transactionType === 1) {
                    openingBalance = first_mainAmount - first_transactionAmount;
                } else if (first_transactionType === 2) {
                    openingBalance = first_mainAmount + first_transactionAmount;
                }
            }

            return openingBalance;
        }
        return 0;
    };

    // Convert PHP week grouping logic
    const groupTradeOrdersByWeek = (dataa) => {
        const groupedTradeOrders = {};
        
        // Handle both array and object responses
        let dataArray = Array.isArray(dataa) ? dataa : (dataa && typeof dataa === 'object' ? Object.values(dataa) : []);
        
        dataArray.forEach(tradeOrder => {
            const orderDate = new Date(tradeOrder.dateTime);
            const orderDateTimestamp = orderDate.getTime();
            
            // PHP: date('W') - ISO week number
            let week_number = getWeekNumber(tradeOrder.dateTime);
            
            // PHP: date('N') - day of week (1=Monday, 7=Sunday)
            const dayNumberOfWeek = getDayNumberOfWeek(tradeOrder.dateTime);
            
            // For Sunday-Saturday weeks, week calculation is handled by getStartDateOfWeek
            // Week starts from Sunday and ends on Saturday
            
            // PHP: date('o') - ISO year
            const year = getISOYear(tradeOrder.dateTime);
            
            // PHP: getStartDateOfWeek and getEndDateOfWeek
            const weekStartDate = getStartDateOfWeek(week_number, year);
            // console.log("weekStartDate", week_number+" : "+year+" : "+weekStartDate);
            const weekEndDate = getEndDateOfWeek(week_number, year);
            // console.log("weekEndDate", week_number+" : "+year+" : "+weekEndDate);
            
            const weekStartDate_formatted = formatDate(weekStartDate);
            const weekEndDate_formatted = formatDate(weekEndDate);

            tradeOrder.weekStartDate = weekStartDate;
            tradeOrder.weekEndDate = weekEndDate;
            tradeOrder.week_number = week_number;
            tradeOrder.year = year;
            tradeOrder.weekStartDate_formatted = weekStartDate_formatted;
            tradeOrder.weekEndDate_formatted = weekEndDate_formatted;
            
            const unique_week_key = `${year}_${week_number}`;

            if (tradeOrder.isTrade == 0) {
                // PHP: Non-trade orders - ALWAYS add to week (no condition check)
                if (!groupedTradeOrders[unique_week_key]) {
                    groupedTradeOrders[unique_week_key] = [];
                }
                groupedTradeOrders[unique_week_key].push(tradeOrder);
            } else {
                // PHP: Trade orders - check if within week range
                // Week starts from Sunday and ends on Saturday
                const dayOfWeek = orderDate.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
                
                // Calculate week start (last Sunday)
                const lastSunday = new Date(orderDate);
                const daysToSunday = dayOfWeek === 0 ? 0 : dayOfWeek; // If Sunday, no adjustment needed
                lastSunday.setDate(orderDate.getDate() - daysToSunday);
                lastSunday.setHours(0, 0, 0, 0);
                const weekStart = lastSunday.getTime();
                
                // Calculate week end (Saturday of the same week)
                let weekEnd;
                if (dayOfWeek === 0) {
                    // If Sunday, get next Saturday (Sunday + 6 = Saturday)
                    const nextSaturday = new Date(orderDate);
                    nextSaturday.setDate(orderDate.getDate() + 6);
                    nextSaturday.setHours(23, 59, 59, 999);
                    weekEnd = nextSaturday.getTime();
                } else {
                    // For Monday to Saturday, get Saturday of the same week
                    const thisSaturday = new Date(orderDate);
                    const daysToSaturday = 6 - dayOfWeek; // Days remaining to reach Saturday
                    thisSaturday.setDate(orderDate.getDate() + daysToSaturday);
                    thisSaturday.setHours(23, 59, 59, 999);
                    weekEnd = thisSaturday.getTime();
                }
                
                // PHP: if ($orderDate >= $weekStart && $orderDate <= $weekEnd)
                if (orderDateTimestamp >= weekStart && orderDateTimestamp <= weekEnd) {
                    // PHP: Check if current week trade
                    const today = new Date();
                    let current_week = getWeekNumber(today.toISOString());
                    let current_year = getISOYear(today.toISOString());
                    
                    // For Sunday-Saturday weeks, adjust week number if needed
                    const todayDayOfWeek = today.getDay();
                    // Week starts from Sunday, so we use the week calculation from getStartDateOfWeek
                    // The week number should already be correct based on Sunday start
                    
                    // PHP: if($week_number == $current_week && $year == $current_year)
                    if (week_number == current_week && year == current_year) {
                        tradeOrder.currentWeekTrade = 1;
                    }

                    // Add trade order to week group
                    if (!groupedTradeOrders[unique_week_key]) {
                        groupedTradeOrders[unique_week_key] = [];
                    }
                    groupedTradeOrders[unique_week_key].push(tradeOrder);
                }
            }
        });

        return groupedTradeOrders;
    };

    const processData = (dataa) => {
        // Handle both array and object responses from API
        let dataArray = Array.isArray(dataa) ? dataa : (dataa && typeof dataa === 'object' ? Object.values(dataa) : []);
        setData(dataArray);
        setOpeningBalance(calculateOpeningBalance(dataArray));
        setGroupedTradeOrders(groupTradeOrdersByWeek(dataArray));
    };

    // Convert PHP tableToCSV function
    const tableToCSV = () => {
        let csv_data = [];
        const rows = document.getElementsByTagName('tr');
        for (let i = 0; i < rows.length; i++) {
            const cols = rows[i].querySelectorAll('td, th');
            let csvrow = [];
            for (let j = 0; j < cols.length; j++) {
                if (cols[j].innerHTML.includes('open')) continue;
                csvrow.push(cols[j].innerHTML);
            }
            csv_data.push(csvrow.join(","));
        }
        csv_data = csv_data.join('\n');
        downloadCSVFile(csv_data);
    };

    // Convert PHP downloadCSVFile function
    const downloadCSVFile = (csv_data) => {
        const CSVFile = new Blob([csv_data], { type: "text/csv" });
        const temp_link = document.createElement('a');
        const userId = localStorage.getItem('userId');
        const userName = localStorage.getItem('userName') || 'User';
        const dateStr = new Date().toLocaleString('en-GB', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).replace(/[\/\s:]/g, '_');
        temp_link.download = `Ledger ${userName} ${userId} ${dateStr}.csv`;
        const url = window.URL.createObjectURL(CSVFile);
        temp_link.href = url;
        temp_link.style.display = "none";
        document.body.appendChild(temp_link);
        temp_link.click();
        document.body.removeChild(temp_link);
    };

    // Convert PHP table rendering logic to card-based mobile-friendly layout
    const renderLedgerTable = () => {
        let currentOpeningBalance = openingBalance;
        let rowIndex = 1;

        const cardStyle = {
            backgroundColor: '#ffffff',
            borderRadius: '6px',
            padding: isMobile ? '8px' : '10px',
            marginBottom: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
        };

        const weekCardStyle = {
            ...cardStyle,
            backgroundColor: '#f9f9f9',
            borderLeft: '3px solid #319197',
        };

        return (
            <div className="ledger-cards-container" style={{ padding: isMobile ? '0px' : '12px' }}>
                {/* Opening Balance Card */}
                <div style={cardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', marginBottom: '2px' }}>#{rowIndex++}</div>
                            <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '600', color: '#000' }}>Opening Balance</div>
                        </div>
                        <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#000' }}>
                            {round(currentOpeningBalance, 2)}
                        </div>
                    </div>
                </div>

                    {/* Process each week - sort by year_week key */}
                    {Object.keys(groupedTradeOrders).sort((a, b) => {
                        // Sort by year first, then by week number
                        const [yearA, weekA] = a.split('_').map(Number);
                        const [yearB, weekB] = b.split('_').map(Number);
                        if (yearA !== yearB) return yearA - yearB;
                        return weekA - weekB;
                    }).map(weekKey => {
                const weekOrder = groupedTradeOrders[weekKey];
                        const weekRows = [];

                        // Process individual orders in the week
                        weekOrder.forEach(order => {
                            if (order.isTrade == 0 || order.currentWeekTrade == 1) {
                                const tradeDate_formatted = formatDate(order.dateTime);
                                let debit = "";
                                let credit = "";
                                let mainAmount = 0;
                                let amount = 0;
                                let isDebit = false;

                                // Calculate debit/credit based on transaction type and user role
                                if (userRole !== "B") {
                                    if (order.transactionType == 2) {
                                        debit = `-${round(order.transactionAmount, 2)}`;
                                        amount = Number(order.transactionAmount);
                                        isDebit = true;
                                    } else if (order.transactionType == 3) {
                                        if (order.jvTransactionType == 2) {
                                            debit = `-${round(order.transactionAmount, 2)}`;
                                            amount = Number(order.transactionAmount);
                                            isDebit = true;
                                        }
                                    }
                                } else {
                                    if (order.transactionType == 1) {
                                        debit = round(order.transactionAmount, 2);
                                        amount = Number(order.transactionAmount);
                                        isDebit = true;
                                    }
                                }

                                if (userRole !== "B") {
                                    if (order.transactionType == 1) {
                                        credit = round(order.transactionAmount, 2);
                                        amount = Number(order.transactionAmount);
                                        isDebit = false;
                                    } else if (order.transactionType == 3) {
                                        if (order.jvTransactionType == 1) {
                                            credit = round(order.transactionAmount, 2);
                                            amount = Number(order.transactionAmount);
                                            isDebit = false;
                                        }
                                    }
                                } else {
                                    if (order.transactionType == 2) {
                                        credit = round(order.transactionAmount, 2);
                                        amount = Number(order.transactionAmount);
                                        isDebit = false;
                                    }
                                }

                                // Calculate balance
                                if (order.transactionType == 1) {
                                    mainAmount = currentOpeningBalance + Number(order.transactionAmount);
                                    currentOpeningBalance = currentOpeningBalance + Number(order.transactionAmount);
                                } else if (order.transactionType == 2) {
                                    mainAmount = currentOpeningBalance - Number(order.transactionAmount);
                                    currentOpeningBalance = currentOpeningBalance - Number(order.transactionAmount);
                                } else if (order.transactionType == 3) {
                                    if (order.jvTransactionType == 1) {
                                        mainAmount = currentOpeningBalance + Number(order.transactionAmount);
                                        currentOpeningBalance = currentOpeningBalance + Number(order.transactionAmount);
                                    } else if (order.jvTransactionType == 2) {
                                        mainAmount = currentOpeningBalance - Number(order.transactionAmount);
                                        currentOpeningBalance = currentOpeningBalance - Number(order.transactionAmount);
                                    }
                                }

                                weekRows.push(
                                    <div key={order.transactionId} style={cardStyle}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', marginBottom: '2px' }}>
                                                    #{rowIndex++} • {tradeDate_formatted}
                                                </div>
                                                <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '500', color: '#000', lineHeight: '1.3' }}>
                                                    {order.manualRemark}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e0e0e0' }}>
                                            {amount > 0 && (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                    <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', marginBottom: '1px' }}>
                                                        {isDebit ? 'Debit' : 'Credit'}
                                                    </div>
                                                    <div style={{ 
                                                        fontSize: isMobile ? '14px' : '16px', 
                                                        fontWeight: '600',
                                                        color: isDebit ? '#d32f2f' : '#2e7d32'
                                                    }}>
                                                        {isDebit ? `-${round(amount, 2)}` : `+${round(amount, 2)}`}
                                                    </div>
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                                <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', marginBottom: '1px' }}>
                                                    Balance
                                                </div>
                                                <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#000' }}>
                                                    {round(mainAmount, 2)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                        });

                        // Calculate week total
                        let week_total = 0;
                        weekOrder.forEach(order => {
                            if (order.isTrade == 0 || order.currentWeekTrade == 1) {
                                return; // Skip non-trade and current week trades
                            }
                            if (order.transactionType == 1) {
                                week_total = week_total + Number(order.transactionAmount);
                            } else if (order.transactionType == 2) {
                                week_total = week_total - Number(order.transactionAmount);
                            }
                        });

                        // Get current week to skip it
                        // PHP: $current_week = date('W', strtotime(date('Y-m-d')));
                        const today = new Date();
                        let current_week = getWeekNumber(today.toISOString());
                        
                        // PHP: if(date('D') == "Sat" || date('D') == "Sun") $current_week++;
                        // For Sunday-Saturday weeks, week calculation is handled by getStartDateOfWeek
                        // The week number should already be correct based on Sunday start
                        const todayDayOfWeek = today.getDay();
                        // Week starts from Sunday, so week number calculation is already correct

                        // PHP: if($weekKey==$current_week) continue;
                        // Need to compare with year_week format
                        const current_year = getISOYear(today.toISOString());
                        const currentWeekKey = `${current_year}_${current_week}`;

                        // Don't show week summary for current week
                        if (weekKey === currentWeekKey) {
                            return <React.Fragment key={weekKey}>{weekRows}</React.Fragment>;
                        }

                        // Week summary card - always show for past weeks
                        const mainAmountAfterWeek = currentOpeningBalance + week_total;
                        currentOpeningBalance = currentOpeningBalance + week_total;

                        weekRows.push(
                            <div key={`week-${weekKey}`} style={weekCardStyle}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', marginBottom: '2px' }}>
                                            #{rowIndex++} • {weekOrder[0].weekEndDate_formatted}
                                        </div>
                                        <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '600', color: '#000' }}>
                                            WEEK: {weekOrder[0].weekStartDate_formatted} - {weekOrder[0].weekEndDate_formatted}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #e0e0e0' }}>
                                    {week_total !== 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                            <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', marginBottom: '1px' }}>
                                                Week Total
                                            </div>
                                            <div style={{ 
                                                fontSize: isMobile ? '14px' : '16px', 
                                                fontWeight: '600',
                                                color: week_total < 0 ? '#d32f2f' : '#2e7d32'
                                            }}>
                                                {week_total < 0 ? round(week_total, 2) : `+${round(week_total, 2)}`}
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#666', marginBottom: '1px' }}>
                                            Balance
                                        </div>
                                        <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: '700', color: '#000' }}>
                                            {round(mainAmountAfterWeek, 2)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );

                        return <React.Fragment key={weekKey}>{weekRows}</React.Fragment>;
                    })}
            </div>
        );
    };

    return (
        <main id="main" className="main ledger-main" style={{ marginLeft: '0px', padding: isMobile ? '10px' : '20px' }}>

            <section className="section" style={{ padding: isMobile ? '10px 0' : '20px 0' }}>
                <div className="row">
                    <div className="col-lg-12">
                        {renderLedgerTable()}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Ledger;
